/**
 * FigJam Plugin Dialog Component
 * 
 * A dialog that informs users they need to manually open the Ideoz plugin in FigJam.
 * Used when the user wants to generate designs directly in FigJam.
 * 
 * @module FigJamPluginDialog
 * @category Molecules
 */

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../atoms/dialog';
import { Button } from '../atoms/button';
import Icon from './Icon';
import { X, NotepadText, ArrowRight, ArrowLeft } from 'lucide-react';

export interface FigJamPluginDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;

  /**
   * Callback when the dialog should close
   */
  onClose: () => void;

  /**
   * Callback when user clicks "Open FigJam Plugin"
   * This should open FigJam and set up the user action
   */
  onOpenFigJamPlugin: () => void;

  /**
   * Callback when user clicks "Cancel"
   */
  onCancel: () => void;
}

/**
 * FigJam Plugin Dialog
 * 
 * Shows instructions for opening the Ideoz plugin manually in FigJam.
 * Displays:
 * - Banner image showing the plugin location
 * - FigJam empty state image
 * - Instructions text
 * - Cancel and "Open FigJam Plugin" buttons
 */
export function FigJamPluginDialog({
  isOpen,
  onClose,
  onOpenFigJamPlugin,
  onCancel,
}: FigJamPluginDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[480px]">
        {/* Header with window controls */}
        <DialogHeader className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon icon={NotepadText} size={16} strokeWidth={2} />
            <DialogTitle>Notes</DialogTitle>
          </div>
          <button onClick={onClose} className="dialog-close-button">
            <Icon icon={X} color="#667085" strokeWidth={2.5} />
          </button>
        </DialogHeader>

        {/* Main content */}
        <div className="flex flex-col items-center text-center gap-4">
          <div className="flex flex-col items-center text-center gap-1">
            <div>
              <img
                width={280}
                height={58.5}
                src="https://test-ideoz-files.sieraj.com/panner.png"
                alt="Plugin location banner"
              />
            </div>
            <div>
              <img
                width={280}
                height={181}
                src="https://test-ideoz-files.sieraj.com/figjam-empty.png"
                alt="FigJam empty state"
              />
            </div>
          </div>

          {/* Title */}
          <div className="flex flex-col items-center text-center gap-2 px-3">
            <h3 className="text-sm font-semibold text-gray-900">
              You need to open Ideoz Plugin Manually
            </h3>
            <p className="text-[10px] text-gray-800 max-w-sm leading-relaxed">
              You will need to open Ideoz plugin manually from your plugins space or by select it
              from the Plugins drop down in your menu.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end pt-2 w-full">
            <Button
              variant="outline"
              size="xs"
              onClick={onCancel}
              className="px-6 text-gray-600 border-gray-300"
            >
              <Icon icon={ArrowLeft} size={20} strokeWidth={2} />
              Cancel
            </Button>
            <Button variant="primary" size="xs" onClick={onOpenFigJamPlugin} className="">
              Open FigJam Plugin
              <Icon icon={ArrowRight} size={20} strokeWidth={2} color="#ffffff" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FigJamPluginDialog;
